import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { useForm, useWatch } from 'react-hook-form';
import styled from 'styled-components';

import { FIELD_KEYS } from '_constants/forms';
import { ICON_ALIASES } from '_constants/icons';
import { theme } from '_constants/theme';
import { getFieldProps } from '_helpers/forms/fields';

import Button from 'components/atomic/Button';
import Icon from 'components/atomic/Icon';
import Spinner from 'components/atomic/Spinner';
import BaseField from 'components/atomic/fields/BaseField';
import DateField from 'components/atomic/fields/DateField';
import SelectField from 'components/atomic/fields/SelectField';
import YearField from 'components/atomic/fields/YearField';
import Text from 'components/atomic/layouts/Text';

const StyledForm = styled(Form)`
  width: 100%;
`;

const StyledMessageContainer = styled.div`
  display: inline-flex;
  justify-content: flex-start;
  align-items: center;
  gap: ${theme.spaces.regular}px;
`;

const UploadDocumentForm = ({ loading, error, submitDisabled, fileTypeOptions, defaultValues, onCancel, onSubmit }) => {
  const {
    control,
    formState: { isSubmitted, isSubmitSuccessful, submitCount },
    handleSubmit,
  } = useForm({
    defaultValues,
    reValidateMode: 'onChange',
  });

  const values = useWatch({ control });

  useEffect(() => {
    if (isSubmitted && !isSubmitSuccessful && submitCount) {
      onSubmit();
    }
  }, [isSubmitted, isSubmitSuccessful, submitCount, onSubmit]);

  return (
    <StyledForm noValidate onSubmit={handleSubmit(onSubmit)}>
      <Row>
        <Col xs={12}>
          <YearField
            {...getFieldProps(FIELD_KEYS.year)}
            icon={<Icon alias={ICON_ALIASES.calendar} color={theme.colors.gray.secondary} />}
            bottomSpace={theme.spaces.regular}
            control={control}
          />
        </Col>
        <Col xs={12}>
          <DateField
            {...getFieldProps(FIELD_KEYS.startDate, {
              validationOptions: {
                values,
              },
            })}
            icon={<Icon alias={ICON_ALIASES.calendar} color={theme.colors.gray.secondary} />}
            dateFormat="MM/dd/yyyy"
            bottomSpace={theme.spaces.regular}
            control={control}
          />
        </Col>
        <Col xs={12}>
          <DateField
            {...getFieldProps(FIELD_KEYS.endDate, {
              validationOptions: {
                values,
              },
            })}
            icon={<Icon alias={ICON_ALIASES.calendar} color={theme.colors.gray.secondary} />}
            dateFormat="MM/dd/yyyy"
            bottomSpace={theme.spaces.regular}
            control={control}
          />
        </Col>
        <Col xs={12}>
          <BaseField {...getFieldProps(FIELD_KEYS.description)} control={control} bottomSpace={theme.spaces.regular} />
        </Col>
        <Col xs={12}>
          <SelectField
            {...getFieldProps(FIELD_KEYS.fileType)}
            options={fileTypeOptions}
            control={control}
            bottomSpace={theme.spaces.regular}
          />
        </Col>
        <Col xs={12}>
          <BaseField {...getFieldProps(FIELD_KEYS.file)} control={control} bottomSpace={theme.spaces.regular} />
        </Col>
        <Col xs={6}>
          <Button outlined onClick={onCancel}>
            Cancel
          </Button>
        </Col>
        <Col xs={6}>
          <Button type="submit" disabled={submitDisabled} color={theme.colors.indigo.primary}>
            Submit
          </Button>
        </Col>
      </Row>

      {loading && (
        <Text size={theme.fontSizes.small} color={theme.colors.gray.secondary} topSpace={theme.spaces.regular}>
          <StyledMessageContainer>
            <Spinner />
            <div>Uploading...</div>
          </StyledMessageContainer>
        </Text>
      )}

      {error && (
        <Text size={theme.fontSizes.small} color={theme.colors.red.primary} topSpace={theme.spaces.regular}>
          {error?.message}
        </Text>
      )}
    </StyledForm>
  );
};

UploadDocumentForm.propTypes = {
  loading: PropTypes.bool,
  error: PropTypes.object,
  submitDisabled: PropTypes.bool,
  fileTypeOptions: PropTypes.arrayOf(PropTypes.object),
  defaultValues: PropTypes.object,
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
};

export default UploadDocumentForm;
